<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import type { RouteLocationRaw } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useSessionStore } from '@/stores/session'
import { useTenantStore } from '@/stores/tenant'
import BrandMark from '@/ui/BrandMark.vue'
import AppFooter from '@/ui/AppFooter.vue'
import AppButton from '@/ui/AppButton.vue'

const { t } = useI18n()
const route = useRoute()
const session = useSessionStore()
const tenant = useTenantStore()

interface NavItem {
  key: string
  to?: RouteLocationRaw
  href?: string
}

/* Nav mirrors the dashboard tiles: the download area and dealer news link out
   (new tab) when the tenant has a destination, so the header and the tiles
   never disagree. */
const navItems = computed<NavItem[]>(() => {
  const current = tenant.current
  const items: NavItem[] = [
    { key: 'dashboard', to: { name: 'dashboard' } },
    { key: 'newOrder', to: { name: 'new-order' } },
    { key: 'orders', to: { name: 'orders' } },
  ]
  // Download area only where a destination exists (hidden on PLCC).
  if (current.downloadUrl) items.push({ key: 'downloads', to: { name: 'downloads' } })
  if (current.newsUrl) items.push({ key: 'news', href: current.newsUrl })
  if (current.features.styleguide) items.push({ key: 'styleguide', to: { name: 'styleguide' } })
  return items
})

/* Mobile menu (drawer). Below the desktop breakpoint the nav collapses behind a
   toggle; the drawer is a focus-trapped dialog with Escape-to-close, scroll
   lock, and focus return - so it is robust with a keyboard and a screen reader,
   not just a tap target. */
const menuOpen = ref(false)
const drawer = ref<HTMLElement | null>(null)
const toggleButton = ref<HTMLElement | null>(null)

function openMenu(): void {
  menuOpen.value = true
}

function closeMenu(): void {
  menuOpen.value = false
}

function focusablesInDrawer(): HTMLElement[] {
  if (!drawer.value) return []
  return Array.from(
    drawer.value.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  )
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape') {
    closeMenu()
    return
  }
  if (event.key !== 'Tab') return
  const items = focusablesInDrawer()
  if (items.length === 0) return
  const first = items[0]
  const last = items[items.length - 1]
  const active = document.activeElement
  if (event.shiftKey && active === first) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && active === last) {
    event.preventDefault()
    first.focus()
  }
}

watch(menuOpen, async (open) => {
  document.body.style.overflow = open ? 'hidden' : ''
  if (open) {
    document.addEventListener('keydown', onKeydown)
    await nextTick()
    focusablesInDrawer()[0]?.focus()
  } else {
    document.removeEventListener('keydown', onKeydown)
    toggleButton.value?.focus()
  }
})

/* Any navigation closes the drawer (route change from a link tap). */
watch(
  () => route.fullPath,
  () => closeMenu(),
)

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeydown)
  document.body.style.overflow = ''
})

async function onLogout(): Promise<void> {
  // signoutRedirect (or the removeUser fallback) owns navigation.
  await session.logout()
}

/* Sticky elements below the header (the shop catalog controls) offset by
   --header-height; the header's real height varies with breakpoint and
   locale, so it must be measured, not hardcoded. */
const headerEl = ref<HTMLElement | null>(null)
let headerObserver: ResizeObserver | undefined

onMounted(() => {
  if (typeof ResizeObserver !== 'function' || !headerEl.value) return
  headerObserver = new ResizeObserver(() => {
    const height = headerEl.value?.offsetHeight
    if (height) document.documentElement.style.setProperty('--header-height', `${height}px`)
  })
  headerObserver.observe(headerEl.value)
})

onBeforeUnmount(() => headerObserver?.disconnect())
</script>

<template>
  <div class="layout">
    <header ref="headerEl" class="header">
      <div class="header__inner">
        <router-link class="header__brand" :to="{ name: 'dashboard' }" @click="closeMenu">
          <BrandMark />
        </router-link>

        <nav class="header__nav" :aria-label="t(`portal.${tenant.current.id}`)">
          <component
            :is="item.href ? 'a' : 'router-link'"
            v-for="item in navItems"
            :key="item.key"
            class="navlink"
            :to="item.href ? undefined : item.to"
            :href="item.href"
            :target="item.href ? '_blank' : undefined"
            :rel="item.href ? 'noopener' : undefined"
          >
            {{ t(`nav.${item.key}`) }}
          </component>
        </nav>

        <div class="header__actions">
          <AppButton v-if="session.isAuthenticated" variant="ghost" @click="onLogout">
            {{ t('nav.logout') }}
          </AppButton>
        </div>

        <button
          ref="toggleButton"
          type="button"
          class="header__toggle"
          :aria-label="menuOpen ? t('nav.closeMenu') : t('nav.openMenu')"
          :aria-expanded="menuOpen"
          aria-controls="mobile-drawer"
          @click="menuOpen ? closeMenu() : openMenu()"
        >
          <span class="burger" :class="{ 'burger--open': menuOpen }" aria-hidden="true">
            <span></span><span></span><span></span>
          </span>
        </button>
      </div>
    </header>

    <!-- Mobile drawer -->
    <transition name="drawer">
      <div v-if="menuOpen" class="drawer-scrim" @click="closeMenu">
        <div
          id="mobile-drawer"
          ref="drawer"
          class="drawer"
          role="dialog"
          aria-modal="true"
          :aria-label="t('nav.menu')"
          @click.stop
        >
          <nav class="drawer__nav" :aria-label="t(`portal.${tenant.current.id}`)">
            <component
              :is="item.href ? 'a' : 'router-link'"
              v-for="item in navItems"
              :key="item.key"
              class="drawer__link"
              :to="item.href ? undefined : item.to"
              :href="item.href"
              :target="item.href ? '_blank' : undefined"
              :rel="item.href ? 'noopener' : undefined"
              @click="closeMenu"
            >
              {{ t(`nav.${item.key}`) }}
            </component>
          </nav>
          <div v-if="session.isAuthenticated" class="drawer__footer">
            <AppButton variant="ghost" class="drawer__logout" @click="onLogout">
              {{ t('nav.logout') }}
            </AppButton>
          </div>
        </div>
      </div>
    </transition>

    <main id="main" class="main">
      <slot />
    </main>
    <AppFooter />
  </div>
</template>

<style scoped>
.layout {
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  background: var(--color-bg);
}

/* Sticky header with a translucent, blurred backdrop so content scrolling
   beneath reads as depth rather than a hard band. */
.header {
  position: sticky;
  top: 0;
  z-index: var(--z-header);
  background: color-mix(in srgb, var(--color-bg) 82%, transparent);
  backdrop-filter: saturate(1.1) blur(12px);
  border-bottom: 1px solid var(--color-border);
}

@supports not (backdrop-filter: blur(1px)) {
  .header {
    background: var(--color-bg);
  }
}

.header__inner {
  display: flex;
  align-items: center;
  gap: var(--space-5);
  max-width: var(--content-max-width);
  margin: 0 auto;
  padding: var(--space-4) var(--content-padding-x);
}

.header__brand {
  flex-shrink: 0;
  text-decoration: none;
  border-radius: var(--radius-sm);
}

.header__nav {
  display: flex;
  align-items: center;
  gap: clamp(var(--space-3), 1.6vw, var(--space-5));
  margin-right: auto;
}

.navlink {
  display: inline-flex;
  align-items: center;
  min-height: var(--size-touch);
  color: var(--color-text-muted);
  font-size: var(--font-size-xs);
  text-transform: uppercase;
  letter-spacing: var(--tracking-button);
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  border-bottom: 1px solid transparent;
  transition: color var(--transition-fast);
}

.navlink:hover,
.navlink:focus-visible {
  color: var(--color-brand);
}

.navlink.router-link-active {
  color: var(--color-brand);
  border-bottom-color: var(--color-brand);
}

.header__actions {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: var(--space-3);
}

/* Hamburger toggle: hidden on desktop, shown when the horizontal nav collapses */
.header__toggle {
  display: none;
  align-items: center;
  justify-content: center;
  width: var(--size-touch);
  height: var(--size-touch);
  margin-left: auto;
  padding: 0;
  background: none;
  border: 0;
  cursor: pointer;
  color: var(--color-brand);
}

.burger {
  display: block;
  position: relative;
  width: 22px;
  height: 14px;
}

.burger span {
  position: absolute;
  left: 0;
  width: 100%;
  height: 1.5px;
  background: currentColor;
  transition:
    transform var(--transition-fast),
    opacity var(--transition-fast),
    top var(--transition-fast);
}

.burger span:nth-child(1) {
  top: 0;
}
.burger span:nth-child(2) {
  top: 6px;
}
.burger span:nth-child(3) {
  top: 12px;
}

.burger--open span:nth-child(1) {
  top: 6px;
  transform: rotate(45deg);
}
.burger--open span:nth-child(2) {
  opacity: 0;
}
.burger--open span:nth-child(3) {
  top: 6px;
  transform: rotate(-45deg);
}

.main {
  flex: 1;
  width: 100%;
  max-width: var(--content-max-width);
  margin: 0 auto;
  padding: var(--space-8) var(--content-padding-x);
}

/* Drawer (mobile) */
.drawer-scrim {
  position: fixed;
  inset: 0;
  z-index: var(--z-dialog);
  background: rgba(20, 20, 22, 0.44);
  backdrop-filter: blur(2px);
  display: flex;
  justify-content: flex-end;
}

.drawer {
  width: min(360px, 84vw);
  height: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  background: var(--color-surface);
  border-left: 1px solid var(--color-border);
  box-shadow: var(--shadow-md);
  padding: var(--space-8) var(--space-5) var(--space-5);
  /* clear the sticky header height so the top link is not under the notch */
  padding-top: calc(var(--space-8) + env(safe-area-inset-top, 0px));
}

.drawer__nav {
  display: flex;
  flex-direction: column;
}

.drawer__link {
  display: flex;
  align-items: center;
  min-height: var(--size-control);
  padding: var(--space-2) 0;
  color: var(--color-text);
  font-size: var(--font-size-md);
  text-transform: uppercase;
  letter-spacing: var(--tracking-label);
  text-decoration: none;
  cursor: pointer;
  border-bottom: 1px solid var(--color-border);
  transition: color var(--transition-fast);
}

.drawer__link:hover,
.drawer__link:focus-visible {
  color: var(--color-brand);
}

.drawer__link.router-link-active {
  color: var(--color-brand);
}

.drawer__footer {
  margin-top: var(--space-6);
}

.drawer__logout {
  width: 100%;
}

/* Drawer transition (respecting reduced-motion via the global base rule) */
.drawer-enter-active,
.drawer-leave-active {
  transition: opacity var(--transition-fast);
}
.drawer-enter-active .drawer,
.drawer-leave-active .drawer {
  transition: transform var(--transition-brand);
}
.drawer-enter-from,
.drawer-leave-to {
  opacity: 0;
}
.drawer-enter-from .drawer,
.drawer-leave-to .drawer {
  transform: translateX(100%);
}

/* Collapse the horizontal nav into the drawer below the desktop breakpoint.
   1200px matches the content max-width: below it the full German nav (uppercase,
   wide-tracked, six items) cannot fit on one line, so it moves to the drawer. */
@media (max-width: 1200px) {
  .header__nav,
  .header__actions {
    display: none;
  }

  .header__toggle {
    display: inline-flex;
  }

  .header__inner {
    padding: var(--space-3) var(--content-padding-x);
  }

  .main {
    padding: var(--space-6) var(--content-padding-x);
  }
}
</style>
